import { useCallback, useEffect, useRef } from 'react';
import { useListContext } from 'react-admin';

/**
 * Merge a patch into the list's filters without clobbering the other writer.
 *
 * Two components set filters on this page — the status/insight controller and
 * the filter bar (search, status, date range) — and react-admin's `setFilters`
 * replaces the whole object. Each writer used to spread its own captured copy
 * of `filterValues`, so whichever fired second wrote back a snapshot taken
 * before the first one's change: type a search, click an insight, and the
 * search quietly vanished (or the reverse).
 *
 * Reading the current filters from a ref at call time means the merge always
 * happens against what's actually applied, not against a render-old closure.
 *
 * A key set to `undefined` in the patch is removed rather than sent as an empty
 * value, so "clear this filter" is expressible.
 */
export function useFilterPatch(): (patch: Record<string, unknown>) => void {
  const { filterValues, setFilters } = useListContext();

  const latest = useRef(filterValues);
  useEffect(() => {
    latest.current = filterValues;
  }, [filterValues]);

  return useCallback((patch: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...(latest.current ?? {}) };

    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') delete next[key];
      else next[key] = value;
    });

    // Nothing actually changed — don't push a fresh object into react-admin and
    // trigger a refetch for it.
    const before = latest.current ?? {};
    const sameKeys = Object.keys(before).length === Object.keys(next).length;
    if (sameKeys && Object.keys(next).every((k) => before[k] === next[k])) return;

    latest.current = next;
    setFilters(next, {});
  }, [setFilters]);
}
